export interface SituationData {
  slug: string;
  name: string;
  metaTitle: string;
  metaDescription: string;
  heroHeading: string;
  heroSubheading: string;
  heroImage: string;
  category: "reason" | "condition" | "challenge";
  introText: string;
  detailParagraphs: string[];
  benefits: string[];
  closingText: string;
}

export const situations: SituationData[] = [
  // ANY REASON
  {
    slug: "inherited-a-house",
    name: "Inherited a House",
    category: "reason",
    metaTitle: "Inherited a House in Oklahoma? Sell It Fast for Cash | House Kings",
    metaDescription: "Inherited a house in Oklahoma and don't know what to do? House Kings buys inherited properties fast for cash. No repairs, no fees, no hassle. Get your cash offer today!",
    heroHeading: "Inherited a House?",
    heroSubheading: "We'll take the burden off your shoulders with a fair cash offer.",
    heroImage: "/images/situations/inherited-house.jpg",
    introText: "Inheriting a house can feel overwhelming — especially if you live far away, the property needs work, or there are multiple heirs involved. House Kings specializes in buying inherited properties quickly and fairly, so you can move forward without the stress.",
    detailParagraphs: [
      "When you inherit a property, you also inherit its taxes, insurance, maintenance costs, and potential code violations. Many inherited homes have been sitting vacant or are outdated and need significant repairs before they could be listed on the open market.",
      "House Kings eliminates all of those headaches. We buy inherited houses as-is — no cleaning, no repairs, no staging. We handle the paperwork and work with title companies experienced in probate and estate transactions.",
      "Whether the property is in probate, has multiple heirs, or has been sitting vacant for years — we can help. We've worked with families across Oklahoma to provide fair cash offers and close on their timeline."
    ],
    benefits: [
      "No need to clean out or repair the property",
      "We handle complex title and probate issues",
      "Fair cash offer within 24 hours",
      "Close on your timeline — even if probate is pending",
      "No realtor commissions or hidden fees"
    ],
    closingText: "Don't let an inherited property become a financial burden. Call House Kings today and get a no-obligation cash offer for your inherited Oklahoma home."
  },
  {
    slug: "relocating",
    name: "Relocating",
    category: "reason",
    metaTitle: "Relocating from Oklahoma? Sell Your House Fast for Cash | House Kings",
    metaDescription: "Need to sell your Oklahoma house fast because you're relocating? House Kings buys houses for cash with fast closings. No repairs, no fees. Get your offer today!",
    heroHeading: "Are You Relocating?",
    heroSubheading: "Sell your Oklahoma house fast so you can focus on your move.",
    heroImage: "/images/situations/relocating.jpg",
    introText: "Whether you've been transferred for work, are moving closer to family, or just ready for a fresh start — selling your house shouldn't hold you back. House Kings buys houses fast for cash so you can relocate on your schedule, not a realtor's.",
    detailParagraphs: [
      "Relocating is stressful enough without worrying about listing your house, scheduling showings, and waiting months for a buyer. With House Kings, you get a cash offer within 24 hours and can close in as little as 7 days.",
      "We buy your house as-is — no need to make repairs, stage the home, or even clean it out. Pack what you need and leave the rest to us.",
      "We've helped hundreds of Oklahoma families sell quickly when relocating for jobs, military transfers, family needs, and lifestyle changes."
    ],
    benefits: [
      "Close in as little as 7 days",
      "No repairs or cleaning needed",
      "No showings or open houses",
      "Cash in hand before your move",
      "We handle all the paperwork"
    ],
    closingText: "Don't let your old house slow down your new chapter. Call House Kings today for a fast, fair cash offer on your Oklahoma home."
  },
  {
    slug: "upgrading",
    name: "Upgrading Your Home",
    category: "reason",
    metaTitle: "Upgrading Your Home? Sell Your Current House Fast | House Kings",
    metaDescription: "Ready to upgrade your Oklahoma home? Sell your current house fast for cash with House Kings. No contingencies, no delays. Get your cash offer today!",
    heroHeading: "Upgrading Your Home?",
    heroSubheading: "Sell your current house fast so you can move into your dream home.",
    heroImage: "/images/situations/upgrading.jpg",
    introText: "Found your dream home but need to sell your current one first? Don't let your old house hold you back. House Kings can buy your current home for cash so you can make a strong, contingency-free offer on your next property.",
    detailParagraphs: [
      "In a competitive housing market, buyers who need to sell their current home first are at a disadvantage. Sellers prefer offers without contingencies — and that's exactly what you can make when House Kings buys your current home for cash.",
      "We close fast, so you won't miss out on your dream home. No repairs, no showings, no waiting for buyer financing to fall through.",
      "Get cash in hand from selling your current home and use it as a powerful down payment on your upgrade."
    ],
    benefits: [
      "Make a contingency-free offer on your new home",
      "Fast closing to match your purchase timeline",
      "No repairs or staging needed",
      "Cash in hand for your down payment",
      "Zero realtor commissions or fees"
    ],
    closingText: "Ready to upgrade? Let House Kings buy your current home fast so you can move up with confidence. Call us today!"
  },
  {
    slug: "downsizing",
    name: "Downsizing",
    category: "reason",
    metaTitle: "Downsizing in Oklahoma? Sell Your House Fast for Cash | House Kings",
    metaDescription: "Downsizing your Oklahoma home? House Kings buys houses fast for cash. No repairs, no commissions, no stress. Get your fair cash offer today!",
    heroHeading: "Downsizing?",
    heroSubheading: "Sell your larger home fast and move to the right size.",
    heroImage: "/images/situations/downsizing.jpg",
    introText: "Whether the kids have moved out, you're entering retirement, or you simply want a simpler life — downsizing should be exciting, not stressful. House Kings buys homes fast for cash so you can downsize on your terms.",
    detailParagraphs: [
      "Maintaining a large home takes time, energy, and money. When you're ready to downsize, the last thing you want is to spend months listing, showing, and negotiating with buyers.",
      "House Kings makes it simple. We give you a fair cash offer, buy your home as-is, and close when you're ready. No need to declutter, repair, or stage — just take what you want and leave the rest.",
      "Many of our clients use the cash from their sale to purchase a smaller home outright, fund retirement, or simply enjoy life with less stress."
    ],
    benefits: [
      "Sell as-is — no decluttering required",
      "Fair cash offer within 24 hours",
      "Close on your timeline",
      "No commissions, fees, or closing costs",
      "Cash in hand for your next chapter"
    ],
    closingText: "Ready to simplify your life? Call House Kings today and get a cash offer for your Oklahoma home."
  },
  {
    slug: "facing-foreclosure",
    name: "Facing Foreclosure",
    category: "reason",
    metaTitle: "Facing Foreclosure in Oklahoma? Sell Your House Fast | House Kings",
    metaDescription: "Stop foreclosure on your Oklahoma home. House Kings buys houses fast for cash, helping you avoid foreclosure and protect your credit. Get help today!",
    heroHeading: "Facing Foreclosure?",
    heroSubheading: "We can help you avoid foreclosure and protect your credit.",
    heroImage: "/images/situations/foreclosure.jpg",
    introText: "Foreclosure can be terrifying — but you have options. House Kings can buy your home fast for cash, helping you pay off your mortgage, avoid a foreclosure on your record, and move forward with a fresh start.",
    detailParagraphs: [
      "A foreclosure stays on your credit report for up to 7 years and can make it extremely difficult to buy another home, get a car loan, or even rent an apartment. But it doesn't have to come to that.",
      "House Kings specializes in working with homeowners facing foreclosure. We can often close in just days — fast enough to stop the foreclosure process. We'll work directly with your lender if needed to ensure a smooth transaction.",
      "Even if you owe more than your home is worth, we may be able to help through a short sale or other creative solutions. Don't wait until it's too late — the sooner you call, the more options you have."
    ],
    benefits: [
      "Stop foreclosure and protect your credit",
      "Close in as little as 7 days",
      "We work directly with your lender",
      "No fees, commissions, or closing costs",
      "Get cash to help you start fresh"
    ],
    closingText: "Don't let foreclosure ruin your future. Call House Kings today — we can help you find a way out."
  },
  {
    slug: "job-transfer",
    name: "Job Transfer",
    category: "reason",
    metaTitle: "Job Transfer? Sell Your Oklahoma House Fast for Cash | House Kings",
    metaDescription: "Got a job transfer and need to sell your Oklahoma house fast? House Kings buys houses for cash with quick closings. No repairs, no fees. Call today!",
    heroHeading: "Got a Job Transfer?",
    heroSubheading: "Sell your house fast so you can start your new position stress-free.",
    heroImage: "/images/situations/job-transfer.jpg",
    introText: "A job transfer is exciting — but having to sell your house on a tight deadline adds unnecessary stress. House Kings buys houses fast for cash, so you can focus on your new opportunity without worrying about your old home.",
    detailParagraphs: [
      "When your employer says 'move,' they usually mean quickly. You don't have time to list your house, wait for showings, negotiate with buyers, and hope the deal doesn't fall through.",
      "House Kings can give you a cash offer within 24 hours and close in as little as 7 days. That means you can wrap up your sale and head to your new job without looking back.",
      "We buy houses as-is — no repairs, no cleaning, no staging. Just a fast, fair cash offer so you can relocate with confidence."
    ],
    benefits: [
      "Cash offer within 24 hours",
      "Close in as little as 7 days",
      "No repairs, cleaning, or showings",
      "Avoid carrying two mortgages",
      "Simple paperwork — we handle everything"
    ],
    closingText: "Don't let your house hold back your career. Call House Kings today for a fast cash offer!"
  },
  {
    slug: "divorce",
    name: "Going Through a Divorce",
    category: "reason",
    metaTitle: "Selling a House During Divorce in Oklahoma | House Kings",
    metaDescription: "Going through a divorce and need to sell your Oklahoma house? House Kings buys houses fast for cash. Fair offers, quick closings, no stress. Call today.",
    heroHeading: "Going Through a Divorce?",
    heroSubheading: "We make selling the house the easiest part of your divorce.",
    heroImage: "/images/situations/divorce.jpg",
    introText: "Divorce is hard enough without the added stress of selling a house. House Kings can buy your home quickly for cash, helping both parties move forward faster and with less conflict.",
    detailParagraphs: [
      "When a marriage ends, the house is often the biggest shared asset — and the biggest source of conflict. Who keeps it? Who pays for repairs? How long will it take to sell? These questions add stress to an already difficult situation.",
      "House Kings simplifies everything. We make a fair cash offer, split however you and your attorney decide. We close fast — often in less than two weeks — so you can both move on.",
      "We've worked with many Oklahoma families going through divorce. We understand the sensitivity involved and will work respectfully with both parties and their attorneys to ensure a smooth transaction."
    ],
    benefits: [
      "Fast sale to help both parties move on",
      "Fair cash offer — split however you decide",
      "No repairs, showings, or staging",
      "We work with your attorneys",
      "Close in as little as 7 days"
    ],
    closingText: "Let House Kings take the house off your plate so you can focus on moving forward. Call us today."
  },
  {
    slug: "facing-bankruptcy",
    name: "Facing Bankruptcy",
    category: "reason",
    metaTitle: "Facing Bankruptcy? Sell Your Oklahoma House Fast | House Kings",
    metaDescription: "Facing bankruptcy and need to sell your Oklahoma home? House Kings buys houses fast for cash. Protect your assets and get a fresh start. Call today!",
    heroHeading: "Facing Bankruptcy?",
    heroSubheading: "Selling your home for cash can help you get a fresh financial start.",
    heroImage: "/images/situations/bankruptcy.jpg",
    introText: "Bankruptcy is overwhelming, but selling your home for cash can help you pay off debts and get a fresh start. House Kings works with homeowners facing financial hardship to provide fast, fair cash offers.",
    detailParagraphs: [
      "If you're facing bankruptcy, your home may be one of your most valuable assets. Selling it before or during bankruptcy proceedings can help you pay off creditors and potentially keep more of your other assets.",
      "House Kings can close quickly — often faster than the bankruptcy timeline requires. We work with bankruptcy attorneys to ensure the sale is handled properly and in your best interest.",
      "Don't let your home become another burden during an already stressful time. A fast cash sale can give you the funds you need to start rebuilding."
    ],
    benefits: [
      "Cash offer to help pay off debts",
      "Fast closing to meet bankruptcy timelines",
      "We work with your bankruptcy attorney",
      "No repairs, fees, or commissions",
      "Get a fresh financial start"
    ],
    closingText: "Don't let bankruptcy define your future. Call House Kings today and explore your options."
  },
  {
    slug: "health-issues",
    name: "Health Issues",
    category: "reason",
    metaTitle: "Selling Your Oklahoma Home Due to Health Issues | House Kings",
    metaDescription: "Need to sell your Oklahoma home due to health issues? House Kings buys houses fast for cash. No stress, no repairs. Compassionate service. Call today.",
    heroHeading: "Having Health Issues?",
    heroSubheading: "We handle everything so you can focus on what matters most — your health.",
    heroImage: "/images/situations/health-issues.jpg",
    introText: "When health issues strike, the last thing you need is the stress of selling a house. House Kings provides compassionate, fast cash offers so you can focus on your health and your family.",
    detailParagraphs: [
      "Health challenges can change everything in an instant. You may need to move to be closer to medical facilities, transition to assisted living, or simply reduce the financial burden of maintaining a home.",
      "House Kings understands the urgency and sensitivity of these situations. We can close in as little as 7 days, and we'll work around your schedule — whether that means meeting at the hospital, a family member's home, or wherever is most convenient.",
      "We buy homes as-is, so you don't need to worry about repairs, cleaning, or showings. We handle all the details so you can focus on getting better."
    ],
    benefits: [
      "Compassionate, no-pressure approach",
      "Close on your timeline",
      "No repairs, cleaning, or showings",
      "We come to you — wherever you are",
      "Cash in hand to cover medical expenses"
    ],
    closingText: "Your health comes first. Let House Kings handle the house so you can focus on healing. Call us today."
  },

  // ANY CONDITION
  {
    slug: "old-outdated-houses",
    name: "Old, Outdated Houses",
    category: "condition",
    metaTitle: "Sell Your Old, Outdated Oklahoma House Fast for Cash | House Kings",
    metaDescription: "Have an old or outdated house in Oklahoma? House Kings buys houses as-is for cash. No renovations needed. Get your fair cash offer today!",
    heroHeading: "Got an Old, Outdated House?",
    heroSubheading: "We buy houses in any condition — no renovations needed.",
    heroImage: "/images/situations/old-house.jpg",
    introText: "Outdated kitchen? Old plumbing? Shag carpet from the 70s? No problem. House Kings buys old, outdated houses as-is for cash. No renovations, no updates, no hassle.",
    detailParagraphs: [
      "Older homes often need significant updates to attract traditional buyers — new roofs, updated electrical, modern kitchens and bathrooms. Those renovations can cost tens of thousands of dollars and take months to complete.",
      "House Kings skips all of that. We buy your home exactly as it is, regardless of age or condition. We see the potential in every property and price our offers accordingly.",
      "Whether your home was built in the 1940s or the 1990s, if it's outdated and you don't want to invest in renovations — we're your buyer."
    ],
    benefits: [
      "No renovations or updates needed",
      "We buy houses from any era",
      "Fair cash offer based on as-is value",
      "Close in as little as 7 days",
      "No realtor commissions or fees"
    ],
    closingText: "Don't spend thousands updating an old house. Sell it as-is to House Kings and get cash fast. Call today!"
  },
  {
    slug: "new-home",
    name: "New Houses",
    category: "condition",
    metaTitle: "Sell Your New Oklahoma Home Fast for Cash | House Kings",
    metaDescription: "Need to sell a newer home in Oklahoma fast? House Kings buys houses of all ages for cash. Quick closings, no fees. Get your offer today!",
    heroHeading: "Need to Sell a New Home?",
    heroSubheading: "Even new homes sometimes need to sell fast. We can help.",
    heroImage: "/images/situations/new-home.jpg",
    introText: "Just because your home is new doesn't mean selling it is easy. Whether plans changed, finances shifted, or life threw a curveball — House Kings buys newer homes for cash with fast closings.",
    detailParagraphs: [
      "Sometimes life doesn't go as planned. Maybe you built a home and your circumstances changed before you could enjoy it. Maybe you bought new construction and now need to relocate. Whatever the reason, we can help.",
      "House Kings buys homes of all ages — including brand new ones. We offer fair cash prices and can close on your timeline, so you're not stuck with a mortgage on a home you no longer need.",
      "Skip the hassle of listing with a realtor and waiting months for a buyer. Get a cash offer from House Kings today."
    ],
    benefits: [
      "Fair market-based cash offers",
      "No waiting for traditional buyers",
      "Close on your timeline",
      "No commissions or fees",
      "Simple, fast process"
    ],
    closingText: "Don't let a change of plans leave you stuck. Call House Kings for a fast cash offer on your newer home."
  },
  {
    slug: "storm-damage",
    name: "Storm Damage",
    category: "condition",
    metaTitle: "Sell Your Storm Damaged Oklahoma House Fast | House Kings",
    metaDescription: "Storm damaged house in Oklahoma? House Kings buys storm damaged homes as-is for cash. No repairs needed. Get your cash offer today!",
    heroHeading: "House Has Storm Damage?",
    heroSubheading: "Oklahoma storms are tough. Selling your damaged home doesn't have to be.",
    heroImage: "/images/situations/storm-damage.jpg",
    introText: "Oklahoma is no stranger to severe weather. If your home has been damaged by tornadoes, hail, wind, or flooding — House Kings will buy it as-is for cash. No insurance claims to fight, no repairs to make.",
    detailParagraphs: [
      "Dealing with storm damage is exhausting. Between insurance adjusters, contractors, and the emotional toll of seeing your home damaged — the last thing you need is the stress of trying to sell a damaged property on the open market.",
      "Traditional buyers don't want storm-damaged homes, and most lenders won't finance them. But House Kings buys with cash, so financing isn't an issue. We take the property exactly as it is.",
      "Whether it's roof damage from hail, structural damage from a tornado, or flooding — we've seen it all and we'll make you a fair offer."
    ],
    benefits: [
      "No repairs needed — we buy as-is",
      "Cash offer regardless of damage severity",
      "No insurance claim hassles",
      "Close fast — get cash when you need it",
      "We handle all the paperwork"
    ],
    closingText: "Don't fight with insurance companies or contractors. Sell your storm-damaged home to House Kings for cash. Call today!"
  },
  {
    slug: "fire-damaged-home",
    name: "Fire Damage",
    category: "condition",
    metaTitle: "Sell Your Fire Damaged Oklahoma Home Fast for Cash | House Kings",
    metaDescription: "Have a fire damaged home in Oklahoma? House Kings buys fire damaged properties as-is for cash. No repairs, no hassle. Get your cash offer today!",
    heroHeading: "House Has Fire Damage?",
    heroSubheading: "We buy fire-damaged homes as-is. No repairs, no stress.",
    heroImage: "/images/situations/fire-damage.jpg",
    introText: "A house fire is devastating. Whether it was a small kitchen fire or a major blaze, House Kings can buy your fire-damaged property as-is for cash — so you can move forward.",
    detailParagraphs: [
      "Fire damage makes a home nearly impossible to sell on the traditional market. Lenders won't finance fire-damaged properties, and most buyers aren't willing to take on the risk and expense of major repairs.",
      "House Kings buys fire-damaged homes with cash. We don't need financing, and we don't need the home to be repaired first. We'll assess the property and make you a fair offer based on its current condition.",
      "We understand how emotional a house fire can be. Our team will work with you compassionately and make the selling process as simple as possible."
    ],
    benefits: [
      "We buy fire-damaged homes as-is",
      "Cash offer — no financing contingencies",
      "No repair or cleanup required",
      "Close quickly to help you move forward",
      "Compassionate, professional service"
    ],
    closingText: "Don't let fire damage keep you stuck. Call House Kings today for a fair cash offer on your fire-damaged Oklahoma home."
  },
  {
    slug: "termite-damage",
    name: "Termite Damage",
    category: "condition",
    metaTitle: "Sell Your Termite Damaged Oklahoma House Fast | House Kings",
    metaDescription: "Termite damage in your Oklahoma home? House Kings buys houses with termite damage as-is for cash. No repairs needed. Get your offer today!",
    heroHeading: "House Has Termite Damage?",
    heroSubheading: "Termites can be devastating, but selling doesn't have to be.",
    heroImage: "/images/situations/termite-damage.jpg",
    introText: "Termite damage can be expensive to repair and makes traditional home sales nearly impossible. House Kings buys homes with termite damage as-is for cash — no treatment or repairs needed.",
    detailParagraphs: [
      "Termite damage often goes unnoticed until it's extensive. Repairs can cost thousands or even tens of thousands of dollars, and most traditional buyers will walk away the moment a termite inspection reveals damage.",
      "House Kings doesn't require inspections or repairs. We buy your home as-is, termite damage and all. We'll make you a fair cash offer based on the property's current condition.",
      "Whether the damage is minor or has compromised the structure, we can help you sell quickly and move on."
    ],
    benefits: [
      "No termite treatment or repairs needed",
      "We buy as-is regardless of damage level",
      "Fair cash offer within 24 hours",
      "No inspections required",
      "Close fast and move on"
    ],
    closingText: "Don't spend thousands on termite repairs. Sell your home as-is to House Kings. Call today!"
  },
  {
    slug: "house-full-of-trash",
    name: "Full of Stuff or Trash",
    category: "condition",
    metaTitle: "House Full of Stuff or Trash? Sell It Fast for Cash | House Kings",
    metaDescription: "Have a house full of stuff or trash in Oklahoma? House Kings buys houses as-is — we'll handle the cleanup. Get your cash offer today!",
    heroHeading: "House Full of Stuff?",
    heroSubheading: "Leave it all behind. We buy the house AND everything in it.",
    heroImage: "/images/situations/house-full-of-stuff.jpg",
    introText: "Whether it's years of accumulated belongings, a hoarding situation, or a property stuffed with trash — House Kings will buy it as-is. You don't have to clean out a single thing.",
    detailParagraphs: [
      "Cleaning out a house full of stuff is overwhelming, expensive, and time-consuming. Dumpster rentals, hauling services, and the emotional toll of sorting through belongings can cost thousands and take weeks.",
      "House Kings eliminates that entire process. We buy the house exactly as it is — full of furniture, personal items, trash, or whatever is inside. Just take what you want and leave the rest to us.",
      "We've bought houses packed floor to ceiling with belongings. Nothing surprises us, and we never judge. We just make a fair offer and handle the cleanup ourselves."
    ],
    benefits: [
      "No cleanup or cleanout required",
      "We buy the house with everything in it",
      "No judgment — we've seen it all",
      "Fair cash offer within 24 hours",
      "We handle all disposal and cleanup"
    ],
    closingText: "Don't spend weeks cleaning out a house. Sell it as-is to House Kings — stuff and all. Call today!"
  },
  {
    slug: "major-repairs-needed",
    name: "Major Repairs Needed",
    category: "condition",
    metaTitle: "House Needs Major Repairs? Sell It Fast for Cash | House Kings",
    metaDescription: "Oklahoma house needs major repairs? House Kings buys houses needing repairs as-is for cash. No fixing needed. Get your cash offer today!",
    heroHeading: "House Need Major Repairs?",
    heroSubheading: "Don't spend thousands fixing it up. Sell as-is for cash.",
    heroImage: "/images/situations/major-repairs.jpg",
    introText: "Foundation problems? Roof replacement? Plumbing overhaul? Major repairs can cost a fortune and take months. House Kings buys houses needing major repairs as-is for cash — so you don't have to spend a dime.",
    detailParagraphs: [
      "Major repairs are the #1 reason homes sit on the market unsold. Traditional buyers and their lenders require homes to pass inspections, and major issues can kill a deal instantly.",
      "House Kings buys with cash — no inspections, no appraisals, no financing contingencies. We buy homes with foundation issues, bad roofs, old plumbing, faulty electrical, and everything in between.",
      "Instead of spending $20,000-$50,000 on repairs just to list your home, sell it to us as-is and keep that money in your pocket."
    ],
    benefits: [
      "No repairs needed — not even minor ones",
      "We buy houses with any type of damage",
      "No inspections or appraisals required",
      "Save tens of thousands in repair costs",
      "Cash offer and fast closing"
    ],
    closingText: "Stop sinking money into a house you want to sell. Call House Kings for a cash offer today!"
  },
  {
    slug: "problem-tenants",
    name: "Problem Tenants",
    category: "condition",
    metaTitle: "Tired of Problem Tenants? Sell Your Rental Property Fast | House Kings",
    metaDescription: "Dealing with problem tenants in Oklahoma? House Kings buys rental properties as-is — even with tenants in place. Get your cash offer today!",
    heroHeading: "Tired of Being a Landlord?",
    heroSubheading: "Sell your rental property fast — even with tenants in place.",
    heroImage: "/images/situations/problem-tenants.jpg",
    introText: "Problem tenants can turn your investment property into a nightmare. Late rent, property damage, eviction battles — House Kings can buy your rental property as-is, even with tenants still in place.",
    detailParagraphs: [
      "Being a landlord isn't for everyone. Between maintenance calls at 2 AM, tenants who don't pay rent, and the constant worry about property damage — many landlords are ready to walk away.",
      "House Kings buys rental properties with or without tenants. We handle the tenant situation so you don't have to go through an expensive and time-consuming eviction process.",
      "Whether you have one rental or a portfolio of properties, we can make you a fair cash offer and close quickly — freeing you from the burden of being a landlord."
    ],
    benefits: [
      "We buy with tenants in place",
      "No need for evictions",
      "Fair cash offer on rental properties",
      "Close fast and stop the headaches",
      "We handle everything"
    ],
    closingText: "Ready to stop being a landlord? Call House Kings today and sell your rental property for cash."
  },
  {
    slug: "unfinished-construction",
    name: "Unfinished Construction",
    category: "condition",
    metaTitle: "Sell Your Unfinished Construction Property in Oklahoma | House Kings",
    metaDescription: "Have an unfinished construction project in Oklahoma? House Kings buys incomplete properties as-is for cash. Get your fair cash offer today!",
    heroHeading: "Unfinished Construction?",
    heroSubheading: "We buy incomplete projects as-is. No need to finish.",
    heroImage: "/images/situations/unfinished-construction.jpg",
    introText: "Ran out of budget? Contractor walked away? Life changed? Whatever the reason your construction project stalled, House Kings will buy it as-is for cash. No need to finish what you started.",
    detailParagraphs: [
      "Unfinished construction projects are nearly impossible to sell on the traditional market. Banks won't finance them, inspectors can't certify them, and most buyers won't touch them.",
      "House Kings buys unfinished projects with cash. Whether it's a half-built addition, a gutted renovation, or a new build that never got finished — we'll make you a fair offer.",
      "Stop paying taxes, insurance, and carrying costs on a project that's going nowhere. Sell it to us and put the cash toward your next venture."
    ],
    benefits: [
      "No need to finish construction",
      "We buy projects in any stage",
      "Cash offer — no financing needed",
      "Stop paying carrying costs",
      "Fair offers on incomplete properties"
    ],
    closingText: "Don't let an unfinished project drain your finances. Call House Kings for a cash offer today!"
  },

  // ANY CHALLENGE
  {
    slug: "financial-issues",
    name: "Financial Issues",
    category: "challenge",
    metaTitle: "Financial Issues? Sell Your Oklahoma House Fast for Cash | House Kings",
    metaDescription: "Facing financial difficulties and need to sell your Oklahoma home? House Kings buys houses fast for cash. No fees, no stress. Get help today!",
    heroHeading: "Facing Financial Issues?",
    heroSubheading: "A fast cash sale can help you get back on solid ground.",
    heroImage: "/images/situations/financial-issues.jpg",
    introText: "Financial problems can feel overwhelming. Whether it's job loss, medical bills, or mounting debt — selling your home for cash can provide the relief you need. House Kings offers fast, fair cash offers with no fees or commissions.",
    detailParagraphs: [
      "When financial issues pile up, your home can go from your biggest asset to your biggest burden. Mortgage payments, property taxes, insurance, and maintenance costs add up fast.",
      "House Kings can help you convert your home equity into cash quickly. We close in as little as 7 days, giving you the funds you need to pay off debts, catch up on bills, or get a fresh start.",
      "There's no judgment here. We've helped hundreds of Oklahoma homeowners through tough financial times, and we treat every client with dignity and respect."
    ],
    benefits: [
      "Fast cash to help pay off debts",
      "Close in as little as 7 days",
      "No fees, commissions, or closing costs",
      "Confidential and respectful process",
      "No repairs or showings needed"
    ],
    closingText: "Don't let financial problems spiral. Call House Kings today and explore your options."
  },
  {
    slug: "title-issues",
    name: "Title Issues",
    category: "challenge",
    metaTitle: "House Has Title Issues? We Still Buy It for Cash | House Kings",
    metaDescription: "Title issues on your Oklahoma property? House Kings can still buy your house for cash. We handle complex title problems. Get your offer today!",
    heroHeading: "House Has Title Issues?",
    heroSubheading: "We specialize in solving title problems so you can sell.",
    heroImage: "/images/situations/title-issues.jpg",
    introText: "Title issues can stop a traditional sale dead in its tracks. But House Kings has experience resolving even the most complex title problems. We'll work with title companies and attorneys to clear the way for a cash sale.",
    detailParagraphs: [
      "Title issues come in many forms — liens, judgments, boundary disputes, missing heirs, clerical errors, or clouded titles from previous transactions. Any of these can prevent a traditional sale.",
      "House Kings has a team of experienced title professionals who specialize in resolving these issues. We've successfully closed on properties with complicated title histories that other buyers walked away from.",
      "Don't assume your property is unsellable because of title problems. In many cases, we can resolve the issues and still close quickly."
    ],
    benefits: [
      "We handle complex title resolution",
      "Experienced title team on staff",
      "Cash offers even with title clouds",
      "We work with attorneys to clear issues",
      "Don't pay for title resolution yourself"
    ],
    closingText: "Title issues don't have to stop you from selling. Call House Kings today — we can help!"
  },
  {
    slug: "code-violations",
    name: "Code Violations",
    category: "challenge",
    metaTitle: "House Has Code Violations? Sell It Fast for Cash | House Kings",
    metaDescription: "Oklahoma house has code violations? House Kings buys houses with code violations as-is for cash. No repairs needed. Get your cash offer today!",
    heroHeading: "House Has Code Violations?",
    heroSubheading: "We buy houses with code violations — no fixes needed.",
    heroImage: "/images/situations/code-violations.jpg",
    introText: "Code violations can make your property impossible to sell through traditional channels. House Kings buys houses with code violations as-is for cash — no repairs, no fines to pay, no headaches.",
    detailParagraphs: [
      "Code violations can range from minor issues like overgrown yards to major structural problems. Either way, they can result in daily fines, liens on your property, and even condemnation if not addressed.",
      "Traditional buyers won't touch a property with code violations, and lenders won't finance them. But House Kings buys with cash, so code violations don't scare us away.",
      "We'll handle the violations after closing — you just walk away with cash in your pocket. We've bought dozens of properties with active code violations across Oklahoma."
    ],
    benefits: [
      "No need to fix code violations",
      "Stop accumulating daily fines",
      "Cash offer regardless of violations",
      "We handle everything after closing",
      "Fast closing to resolve your situation"
    ],
    closingText: "Stop paying fines on a house you want to sell. Call House Kings today for a cash offer!"
  },
  {
    slug: "hoarder-home",
    name: "Hoarder Home",
    category: "challenge",
    metaTitle: "Sell a Hoarder Home in Oklahoma Fast for Cash | House Kings",
    metaDescription: "Need to sell a hoarder home in Oklahoma? House Kings buys hoarder houses as-is for cash. No cleanup needed. Compassionate service. Call today.",
    heroHeading: "Dealing with a Hoarder Home?",
    heroSubheading: "We buy hoarder homes as-is. No cleanup, no judgment.",
    heroImage: "/images/situations/hoarder-home.jpg",
    introText: "Hoarder homes are one of the most challenging properties to deal with — but House Kings has the experience and compassion to handle it. We buy hoarder homes as-is for cash, with no cleanup required.",
    detailParagraphs: [
      "Hoarding is a complex issue that often involves years of accumulated belongings. The cleanup alone can cost thousands of dollars and take weeks. Then there's often hidden damage underneath — mold, pests, structural issues.",
      "House Kings has bought many hoarder homes across Oklahoma. We never judge, and we handle the entire cleanup process after closing. You don't need to touch a single item — just take what you want and leave the rest.",
      "Whether you're dealing with your own hoarding situation or cleaning up after a family member, we're here to help with compassion and a fair cash offer."
    ],
    benefits: [
      "No cleanup or cleanout required",
      "Compassionate, no-judgment approach",
      "Fair cash offer within 24 hours",
      "We handle all cleanup after closing",
      "Experienced with hoarder properties"
    ],
    closingText: "Don't face a hoarder home alone. Call House Kings today for compassionate help and a fair cash offer."
  },
  {
    slug: "low-equity",
    name: "Low Equity",
    category: "challenge",
    metaTitle: "Low Equity on Your Oklahoma Home? We Can Still Help | House Kings",
    metaDescription: "Little or no equity in your Oklahoma home? House Kings may still be able to help you sell. Creative solutions for low equity situations. Call today!",
    heroHeading: "Low Equity on Your Home?",
    heroSubheading: "We have creative solutions for homeowners with little equity.",
    heroImage: "/images/situations/low-equity.jpg",
    introText: "Owing close to what your home is worth — or even more — can feel like a trap. House Kings has creative solutions for low equity situations that can help you move forward.",
    detailParagraphs: [
      "Low equity happens when your mortgage balance is close to or exceeds your home's value. This can occur when property values drop, you bought with a small down payment, or you took out home equity loans.",
      "In a traditional sale, low equity means you'd barely break even after paying realtor commissions and closing costs — or you might even owe money at closing. That's why many homeowners feel stuck.",
      "House Kings eliminates commissions and closing costs, which means more equity stays in your pocket. In some cases, we can also negotiate with your lender for a short sale or other creative arrangement."
    ],
    benefits: [
      "No commissions means more equity for you",
      "We pay all closing costs",
      "Creative solutions for underwater homes",
      "We can negotiate with your lender",
      "Free, no-obligation consultation"
    ],
    closingText: "Low equity doesn't mean no options. Call House Kings today and let's explore what we can do for you."
  },
  {
    slug: "large-liens",
    name: "Large Liens",
    category: "challenge",
    metaTitle: "Property Has Large Liens? We Can Still Buy It | House Kings",
    metaDescription: "Oklahoma property has large liens? House Kings buys houses with liens for cash. We help resolve lien issues. Get your cash offer today!",
    heroHeading: "Property Has Large Liens?",
    heroSubheading: "We buy properties with liens and help resolve them.",
    heroImage: "/images/situations/large-liens.jpg",
    introText: "Liens on your property — whether from taxes, contractors, or judgments — can make selling seem impossible. House Kings has experience buying properties with liens and can help you find a path forward.",
    detailParagraphs: [
      "Liens can come from many sources: unpaid property taxes, IRS tax liens, mechanic's liens from contractors, judgment liens from lawsuits, or HOA liens. Any of these can block a traditional sale.",
      "House Kings works with lien holders to negotiate payoffs, reductions, or payment plans as part of the sale. In many cases, we can still make you a fair offer even after accounting for outstanding liens.",
      "Don't assume your property is unsellable because of liens. We've helped many Oklahoma homeowners sell properties with significant lien amounts and walk away with cash."
    ],
    benefits: [
      "We buy properties with any type of liens",
      "Help negotiating lien payoffs",
      "Cash offers even with outstanding liens",
      "We work with lien holders directly",
      "Get cash and move on with your life"
    ],
    closingText: "Liens don't have to trap you. Call House Kings today and let us help you sell your property."
  },
  {
    slug: "behind-on-payments",
    name: "Behind on Payments",
    category: "challenge",
    metaTitle: "Behind on Mortgage Payments? Sell Your House Fast | House Kings",
    metaDescription: "Behind on mortgage payments in Oklahoma? House Kings buys houses fast for cash. Stop the stress and avoid foreclosure. Get help today!",
    heroHeading: "Behind on Payments?",
    heroSubheading: "Sell fast, pay off your mortgage, and avoid foreclosure.",
    heroImage: "/images/situations/behind-on-payments.jpg",
    introText: "Falling behind on mortgage payments can feel terrifying. Every month the hole gets deeper. House Kings can help you sell your home fast for cash, pay off your mortgage, and avoid the devastating consequences of foreclosure.",
    detailParagraphs: [
      "When you miss mortgage payments, late fees pile up, your credit score drops, and your lender starts the foreclosure process. The longer you wait, the fewer options you have.",
      "House Kings can close in as little as 7 days — fast enough to stop foreclosure proceedings. We'll pay off your remaining mortgage balance at closing and, in many cases, put cash in your pocket too.",
      "Don't wait until the sheriff is at your door. The sooner you call, the more options we have to help you. Many homeowners wish they had called us sooner."
    ],
    benefits: [
      "Sell before foreclosure hits your credit",
      "Close in as little as 7 days",
      "We pay off your remaining mortgage",
      "Potentially walk away with cash",
      "Stop the stress of late payment notices"
    ],
    closingText: "Don't let late payments lead to foreclosure. Call House Kings today — we can help you sell fast."
  },
  {
    slug: "vacant-property",
    name: "Vacant Property",
    category: "challenge",
    metaTitle: "Sell Your Vacant Oklahoma Property Fast for Cash | House Kings",
    metaDescription: "Have a vacant property in Oklahoma? House Kings buys vacant houses fast for cash. Stop paying for a property you don't use. Get your offer today!",
    heroHeading: "Have a Vacant Property?",
    heroSubheading: "Stop paying taxes and insurance on a house sitting empty.",
    heroImage: "/images/situations/vacant-property.jpg",
    introText: "A vacant property is money leaving your pocket every month — property taxes, insurance, lawn care, and the risk of vandalism or squatters. House Kings buys vacant properties fast for cash so you can stop the bleeding.",
    detailParagraphs: [
      "Vacant properties are expensive to maintain and easy targets for vandalism, squatters, and code violations. Insurance companies charge higher rates for vacant homes, and many cities impose additional fees.",
      "Every month your vacant property sits, you're losing money. Property taxes, insurance premiums, utility minimums, and lawn maintenance add up quickly — with zero return on investment.",
      "House Kings can take that burden off your hands with a fair cash offer and a fast closing. Stop spending money on a property you're not using."
    ],
    benefits: [
      "Stop paying taxes and insurance on an empty house",
      "Avoid vandalism and squatter risks",
      "Cash offer within 24 hours",
      "Close fast and stop the monthly costs",
      "No repairs or cleanup needed"
    ],
    closingText: "A vacant house is a money pit. Call House Kings today and turn that empty property into cash."
  },
];

export const getSituationBySlug = (slug: string): SituationData | undefined => {
  return situations.find((s) => s.slug === slug);
};

export const getSituationsByCategory = (category: "reason" | "condition" | "challenge"): SituationData[] => {
  return situations.filter((s) => s.category === category);
};
